import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { TonConnectError, UserRejectsError } from "@tonconnect/sdk"
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react"
import { topUpTon } from "@/shared/api/endpoints"
import { paymentHistoryKeys } from "@/entities/history"
import { walletBalanceQueryKey } from "@/shared/hooks/useWalletBalance"
import { makeCommentPayloadBase64 } from "@/shared/lib/tonCommentPayload"
import { translatePath } from "@/shared/i18n"
import { makeSuccessToast } from "@/shared/lib/toast-success"
import { makeErrorToast } from "@/shared/lib/toast-error"
import {
  AnalyticsEvent,
  analyticsErrorMessage,
  classifyDepositFailure,
  getAnalyticsDepositUiSource,
  registerPendingTonDeposit,
  trackAnalyticsEvent,
} from "@/shared/lib/analytics"

const TX_VALID_UNTIL_SEC = 600

/** SDK prefixes `TonConnectError.message` with this; strip for user-visible toasts */
const TON_CONNECT_SDK_MESSAGE_PREFIX = "[TON_CONNECT_SDK_ERROR] " as const

function toastLabelFromTonConnectError(error: TonConnectError): string {
  const raw = error.message.trim()
  const body = raw.startsWith(TON_CONNECT_SDK_MESSAGE_PREFIX)
    ? raw.slice(TON_CONNECT_SDK_MESSAGE_PREFIX.length).trim()
    : raw
  return body.length > 0 ? body : translatePath("toast.failedTonDeposit")
}

export type UseTonDepositOptions = {
  /** e.g. close DepositSheet */
  onSuccess?: () => void
  /** Default: no-op. Wallet page shows a toast when user taps top-up without a wallet. */
  onMissingWallet?: () => void
}

/**
 * Single place for TON deposit: API invoice → TonConnect sendTransaction → cache invalidation.
 * UI only opens {@link DepositSheet} and passes `onDeposit` / `isProcessing` from here.
 */
export function useTonDeposit(options: UseTonDepositOptions = {}) {
  const optsRef = useRef(options)
  optsRef.current = options

  const [isProcessing, setIsProcessing] = useState(false)
  const queryClient = useQueryClient()
  const [tonConnectUI] = useTonConnectUI()
  const connectedAddress = useTonAddress()

  const depositTon = useCallback(
    async (amountNanotons: number) => {
      const { onSuccess, onMissingWallet } = optsRef.current
      if (!connectedAddress) {
        onMissingWallet?.()
        return
      }
      if (!Number.isFinite(amountNanotons) || amountNanotons <= 0) return
      setIsProcessing(true)
      try {
        const response = await topUpTon({ amount: amountNanotons })
        const { payment_instructions } = response
        const payloadBase64 = makeCommentPayloadBase64(
          payment_instructions.payload
        )
        const transaction = {
          validUntil: Math.floor(Date.now() / 1000) + TX_VALID_UNTIL_SEC,
          messages: [
            {
              address: payment_instructions.destination_address,
              amount: String(payment_instructions.amount),
              payload: payloadBase64,
            },
          ],
        }
        await tonConnectUI.sendTransaction(transaction)
        const amountTon = amountNanotons / 1e9
        registerPendingTonDeposit("ton_connect", amountTon)
        trackAnalyticsEvent(AnalyticsEvent.DepositInitiated, {
          deposit_method: "ton_connect",
          amount_ton: amountTon,
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })
        makeSuccessToast({ label: translatePath("toast.depositInitiated") })
        queryClient.invalidateQueries({
          queryKey: paymentHistoryKeys.infinite(),
        })
        queryClient.invalidateQueries({
          queryKey: walletBalanceQueryKey(connectedAddress),
        })
        onSuccess?.()
      } catch (error) {
        const amountTon = amountNanotons / 1e9
        if (error instanceof UserRejectsError) {
          trackAnalyticsEvent(AnalyticsEvent.DepositFailed, {
            deposit_method: "ton_connect",
            amount_ton: amountTon,
            failure_reason: "user_rejected",
            deposit_ui_source: getAnalyticsDepositUiSource(),
          })
          return
        }
        if (error instanceof TonConnectError) {
          trackAnalyticsEvent(AnalyticsEvent.DepositFailed, {
            deposit_method: "ton_connect",
            amount_ton: amountTon,
            failure_reason: classifyDepositFailure(error),
            error_message: toastLabelFromTonConnectError(error),
            deposit_ui_source: getAnalyticsDepositUiSource(),
          })
          makeErrorToast({ label: toastLabelFromTonConnectError(error) })
          return
        }
        trackAnalyticsEvent(AnalyticsEvent.DepositFailed, {
          deposit_method: "ton_connect",
          amount_ton: amountTon,
          failure_reason: classifyDepositFailure(error),
          error_message: analyticsErrorMessage(error),
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })
        if (error instanceof Error) {
          makeErrorToast({
            label: error.message || translatePath("toast.failedTonDeposit"),
          })
        } else {
          makeErrorToast({
            label: translatePath("toast.transactionCancelled"),
          })
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [connectedAddress, queryClient, tonConnectUI]
  )

  return { depositTon, isProcessing, connectedAddress }
}
